import { AppConstants } from '@/helpers/app.constants';
import { PropertyHelper } from '@/helpers/property.helper';
import { TrackingHelper } from '@/helpers/tracking.helper';
import { AlertService } from '@/services/alert.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import {
  Attribute,
  DataDictionaryService,
  MaintenanceFrequency,
  MaintenanceReference,
  MaintenanceType
} from '@eemo/api-client';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { InputUnitDirective } from '../../extensions/inputUnitDirective';
import { InputDecimalValueDirective } from '../../extensions/inputDecimalValueDirective';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';

import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { NgIf, NgFor, NgSwitch, NgSwitchCase, NgSwitchDefault, AsyncPipe } from '@angular/common';
import { InputTrimDirective } from '../../extensions/inputTrimDirective';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

export class MaintenanceReferenceDialodData {
  maint: MaintenanceReference;
  edition = false;
}

@Component({
    selector: 'app-maintenance-reference-dialog',
    templateUrl: './maintenance-reference-dialog.component.html',
    styleUrls: ['./maintenance-reference-dialog.component.scss'],
    standalone: true,
    imports: [MatDialogModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, InputTrimDirective, NgIf, MatSelectModule, NgFor, MatOptionModule, MatSlideToggleModule, MatIconModule, MatAutocompleteModule, NgSwitch, NgSwitchCase, InputDecimalValueDirective, InputUnitDirective, NgSwitchDefault, MatDividerModule, MatButtonModule, AsyncPipe]
})
export class MaintenanceReferenceDialogComponent implements OnInit {
  form: UntypedFormGroup;
  formSubmitting = false;
  dialogTitle: string;

  attributes: Attribute[] = [];
  filteredAttributes: Observable<Attribute[]>[] = [];
  selectedAttributeSubject: BehaviorSubject<Attribute>[] = [];
  selectedAttribute: Observable<Attribute>[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MaintenanceReferenceDialodData,
    private dictService: DataDictionaryService,
    private fb: UntypedFormBuilder,
    private alertService: AlertService,
    public dialogRef: MatDialogRef<MaintenanceReferenceDialogComponent>,
    public propHelper: PropertyHelper,
    public trackingHelper: TrackingHelper
  ) {
    this.dialogTitle = this.data.edition ? 'Modifier la maintenance' : 'Créer une nouvelle maintenance';
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.form.get(field);
  }

  ngOnInit() {
    this.form = this.fb.group({
      summary: ['', [Validators.required, Validators.maxLength(50)]],
      type: [MaintenanceType.BUILDING, Validators.required],
      frequency: [MaintenanceFrequency.YEAR, Validators.required],
      energyImpact: [false],
      description: ['', Validators.maxLength(160)],
      propertyTypes: [[]],
      conditions: this.fb.array([])
    });

    if (this.data.maint) {
      if (this.data.maint.conditions) {
        this.data.maint.conditions.forEach((cond) =>
          this.conditions.push(this.createConditionFormItem(cond.attributeCode))
        );
      }
      this.form.patchValue(this.data.maint);
    }

    this.dictService.getDictionaryAttributes().subscribe((attrs) => {
      this.attributes = attrs;
    });
  }

  get conditions(): UntypedFormArray {
    return this.form.get('conditions') as UntypedFormArray;
  }

  selectAttribute(code: string, index: number, event: any) {
    if (event.isUserInput) {
      // ignore on deselection of the previous option
      this.selectedAttributeSubject[index].next(this.findAttributeFromCode(code));
    }
  }

  findAttributeFromCode(code: string): Attribute {
    return this.attributes.find((a) => a.code === code);
  }

  getAutocompleteValuesForAttribute(index: number) {}

  createConditionFormItem(code: string) {
    const index = this.conditions.length;
    this.selectedAttributeSubject[index] = new BehaviorSubject<Attribute>(this.findAttributeFromCode(code));
    this.selectedAttribute[index] = this.selectedAttributeSubject[index].asObservable();
    const form = this.fb.group({
      attributeCode: ['', [Validators.required, Validators.pattern(AppConstants.FORM_PATTERN_CODE)]],
      attributeValue: ['']
    });
    this.filteredAttributes[this.conditions.length] = form.get('attributeCode').valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || ''))
    );
    return form;
  }

  private _filter(value: string): Attribute[] {
    const filterValue = value.toLowerCase();
    return this.attributes.filter((attr) => attr.code.toLowerCase().includes(filterValue));
  }

  addCondition(): void {
    this.conditions.push(this.createConditionFormItem(''));
  }

  removeCondition(i: number) {
    this.conditions.removeAt(i);
    delete this.filteredAttributes[i];
  }

  onSubmit() {
    if (this.form.valid) {
      this.formSubmitting = true;
      if (this.data.edition) {
        this.dictService.updateDictionaryMaintenance(this.data.maint.uid, this.form.value).subscribe(
          (result) => {
            this.dialogRef.close(true);
            this.alertService.success('Modification de la maintenance effectuée');
          },
          (err: HttpErrorResponse) => {
            this.dialogRef.close(false);
          }
        );
      } else {
        this.dictService.createDictionaryMaintenance(this.form.value).subscribe(
          (result) => {
            this.dialogRef.close(true);
            this.alertService.success('Création de la maintenance effectuée');
          },
          (err: HttpErrorResponse) => {
            this.dialogRef.close(false);
          }
        );
      }
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
